import type { Creator } from "@coinfallsmp/shared";
import { creatorBadge } from "@coinfallsmp/shared";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { createCreator, listCreators } from "../api.js";
import CreatorBadge from "../components/CreatorBadge.js";
import StatusBadge from "../components/StatusBadge.js";

export default function Creators() {
  const [creators, setCreators] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await listCreators();
      setCreators(res.items);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <section>
      <div className="page-header">
        <h1>Creators</h1>
        <div className="header-actions">
          <Link to="/applications" className="button secondary">
            Applications
          </Link>
          <button className="button primary" onClick={() => setShowForm((s) => !s)}>
            {showForm ? "Cancel" : "Invite creator"}
          </button>
        </div>
      </div>

      {showForm ? (
        <InviteCreatorForm
          onCreated={() => {
            setShowForm(false);
            load();
          }}
        />
      ) : null}

      {loading ? (
        <p className="status-message">Loading…</p>
      ) : (
        <table className="video-table">
          <thead>
            <tr>
              <th>Creator</th>
              <th>Email</th>
              <th>Status</th>
              <th>Can upload</th>
              <th>Verified</th>
            </tr>
          </thead>
          <tbody>
            {creators.map((creator) => (
              <tr key={creator.id}>
                <td>
                  <Link to={`/creators/${creator.id}`}>
                    <CreatorBadge badge={creatorBadge(creator)} /> {creator.displayName}{" "}
                    <span className="creator-username">@{creator.username}</span>
                  </Link>
                </td>
                <td>{creator.email}</td>
                <td>
                  <StatusBadge status={creator.status} />
                </td>
                <td>{creator.canUpload ? "Yes" : "No"}</td>
                <td>{creator.isVerified ? "Yes" : "No"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function InviteCreatorForm({ onCreated }: { onCreated: () => void }) {
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [bio, setBio] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await createCreator({
        username: username.trim(),
        displayName: displayName.trim(),
        email: email.trim(),
        bio: bio.trim() || undefined,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create creator");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="inline-form" onSubmit={submit}>
      <label>
        Username
        <input value={username} onChange={(e) => setUsername(e.target.value)} required />
      </label>
      <label>
        Display name
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
      </label>
      <label>
        Email (their authenticated identity)
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </label>
      <label>
        Bio (optional)
        <input value={bio} onChange={(e) => setBio(e.target.value)} />
      </label>
      {error ? <p className="form-error">{error}</p> : null}
      <button className="button primary" type="submit" disabled={busy}>
        {busy ? "Creating…" : "Create"}
      </button>
    </form>
  );
}
